import { allocateRefund, type RefundSource } from "./refundAllocation.ts";
import styles from "./editCartModal.module.scss";

/** One line of the operator's refund plan: a payment, whether to use it, in display order. */
export interface RefundPlanEntry {
    id: number;
    selected: boolean;
}

function fmtAmt(n: number): string {
    return n.toFixed(2);
}

/** Which payments a Save & Refund comes from, and in what order. The ticked rows are sent to the
 *  server as RefundPaymentOrder; the "will refund" column is the preview of its split. */
export function RefundSourcesPanel({ sources, plan, amount, onChange }: {
    sources: RefundSource[];
    plan: RefundPlanEntry[];
    amount: number;
    onChange: (plan: RefundPlanEntry[]) => void;
}) {
    const byId = new Map(sources.map(s => [s.id, s]));
    const ordered = plan
        .filter(e => e.selected)
        .map(e => byId.get(e.id))
        .filter((s): s is RefundSource => s !== undefined);
    const { steps, uncovered } = allocateRefund(ordered, amount);
    const willRefund = new Map(steps.map(s => [s.id, s.amount]));
    const usable = sources.filter(s => !s.blocked);

    const shortfall = uncovered > 0 ? (
        <div className={styles.issues}>
            <div className={styles.issueItem}>
                {`The selected payments cover ${fmtAmt(amount - uncovered)} of ${fmtAmt(amount)}; ${fmtAmt(uncovered)} will not be refunded.`}
            </div>
        </div>
    ) : null;

    if (usable.length === 0) {
        return (
            <div className={styles.issues}>
                <div className={styles.issueItem}>No payment on this transaction can be refunded. The edit is saved and nothing is refunded.</div>
            </div>
        );
    }

    if (sources.length === 1) {
        const s = sources[0]!;
        return (
            <>
                <p className={styles.confirmNote}>{`Refund ${fmtAmt(willRefund.get(s.id) ?? 0)} from payment #${s.id} (${s.type}).`}</p>
                {shortfall}
            </>
        );
    }

    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= plan.length) return;
        const next = [...plan];
        [next[i], next[j]] = [next[j]!, next[i]!];
        onChange(next);
    };
    const toggle = (i: number) => onChange(plan.map((e, k) => (k === i ? { ...e, selected: !e.selected } : e)));

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>Refund from</span>
                <span className={styles.badge}>{fmtAmt(amount)}</span>
            </div>
            <table className={styles.cartTable}>
                <thead>
                    <tr>
                        <th className={styles.thAction}></th>
                        <th>Payment</th>
                        <th className={styles.thR}>Paid</th>
                        <th className={styles.thR}>Refunded</th>
                        <th className={styles.thR}>Remaining</th>
                        <th className={styles.thR}>Will refund</th>
                        <th className={styles.thOrder}>Order</th>
                    </tr>
                </thead>
                <tbody>
                    {plan.map((e, i) => {
                        const s = byId.get(e.id);
                        if (!s) return null;
                        return (
                            <tr key={s.id} className={s.blocked ? styles.refundBlocked : undefined}
                                title={s.blocked ? "This database's refund filter refuses this payment" : undefined}>
                                <td className={styles.tdAction}>
                                    <input type="checkbox" checked={e.selected} disabled={s.blocked}
                                        aria-label={`Refund from payment #${s.id}`} onChange={() => toggle(i)} />
                                </td>
                                <td>#{s.id} <span className={styles.typeTag}>{s.type}</span></td>
                                <td className={styles.tdR}>{fmtAmt(s.charged)}</td>
                                <td className={styles.tdR}>{fmtAmt(s.refunded)}</td>
                                <td className={styles.tdR}>{fmtAmt(s.remaining)}</td>
                                <td className={styles.tdR}>{e.selected ? fmtAmt(willRefund.get(s.id) ?? 0) : "—"}</td>
                                <td className={styles.tdOrder}>
                                    <button type="button" className={styles.orderBtn} onClick={() => move(i, -1)}
                                        disabled={i === 0} aria-label={`Move payment #${s.id} up`}>↑</button>
                                    <button type="button" className={styles.orderBtn} onClick={() => move(i, 1)}
                                        disabled={i === plan.length - 1} aria-label={`Move payment #${s.id} down`}>↓</button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {shortfall}
        </div>
    );
}
